import React, { useEffect, useRef, useState } from "react";

const AFrameContainer = ({ onCanvasReady, onApi, onArStarted, onPrimeMic }) => {
  const sceneRef = useRef(null);

  const [starting, setStarting] = useState(false);
  const [started, setStarted] = useState(false);
  const [error, setError] = useState("");
  const [shouldFaceUser, setShouldFaceUser] = useState(true);

  const rearStreamRef = useRef(null);
  const rearAssetVideoRef = useRef(null);
  const rearVideoNodeRef = useRef(null);
  useEffect(() => {
    if (typeof window === "undefined" || !window.AFRAME) return;
    const AFRAME = window.AFRAME;

    if (!AFRAME.components["depth-occluder"]) {
      AFRAME.registerComponent("depth-occluder", {
        schema: { renderOrder: { default: 0 } },
        init() {
          const apply = () => {
            const root = this.el.object3D;
            if (!root) return;
            root.traverse((o) => {
              if (o.isMesh && o.material) {
                const mats = Array.isArray(o.material) ? o.material : [o.material];
                mats.forEach((m) => {
                  if (!m) return;
                  m.transparent = false;
                  m.opacity = 1;
                  m.depthTest = true;
                  m.depthWrite = true;
                  if ("colorWrite" in m) m.colorWrite = false;
                  m.needsUpdate = true;
                });
              }
            });
            root.renderOrder = this.data.renderOrder;
          };
          this.el.addEventListener("object3dset", apply);
          this.el.addEventListener("model-loaded", apply);
          apply();
        },
      });
    }

    if (!AFRAME.components["fit-video-plane"]) {
      AFRAME.registerComponent("fit-video-plane", {
        schema: {
          video: { type: "selector" },
          mode: { default: "contain" },
        },
        init() {
          this._onResize = this._onResize.bind(this);
          this._tryFit = this._tryFit.bind(this);
          this._video = null;
          this._meta = null;

          this.el.sceneEl.addEventListener("loaded", this._tryFit);
          window.addEventListener("resize", this._onResize);
          window.addEventListener("orientationchange", this._onResize);
        },
        remove() {
          this.el.sceneEl?.removeEventListener?.("loaded", this._tryFit);
          window.removeEventListener("resize", this._onResize);
          window.removeEventListener("orientationchange", this._onResize);
          if (this._video && this._meta) {
            this._video.removeEventListener("loadedmetadata", this._meta);
            this._meta = null;
          }
        },
        update() { this._tryFit(); },
        _onResize() { this._tryFit(); },
        _attach(video) {
          if (!video) return;
          this._video = video;
          if (this._meta) this._video.removeEventListener("loadedmetadata", this._meta);
          this._meta = () => this._tryFit();
          this._video.addEventListener("loadedmetadata", this._meta, { once: true });
        },
        _frustumSizeAt(camera, zAbs) {
          const fov = (camera.fov ?? 80) * (Math.PI / 180);
          const height = 2 * Math.tan(fov / 2) * zAbs;
          const width = height * camera.aspect;
          return { width, height };
        },
        _tryFit() {
          const sceneEl = this.el.sceneEl;
          const camera = sceneEl?.camera;
          if (!camera) return;

          let video = this.data.video;
          if (!video || !(video instanceof HTMLVideoElement)) {
            const sel = this.attrValue?.video;
            if (sel && typeof sel === "string") video = sceneEl.querySelector(sel);
          }
          if (!video) return;

          if (!video.videoWidth || !video.videoHeight) {
            this._attach(video);
            return;
          }

          const obj = this.el.object3D;
          const zAbs = Math.abs(obj.position.z || 2);
          const { width: frW, height: frH } = this._frustumSizeAt(camera, zAbs);
          const vW = video.videoWidth, vH = video.videoHeight, vAR = vW / vH, frAR = frW / frH;

          let targetW, targetH;
          if (this.data.mode === "cover") {
            if (frAR > vAR) { targetW = frW; targetH = targetW / vAR; }
            else { targetH = frH; targetW = targetH * vAR; }
          } else {
            if (frAR > vAR) { targetH = frH; targetW = targetH * vAR; }
            else { targetW = frW; targetH = targetW / vAR; }
          }

          try {
            this.el.setAttribute("geometry", { primitive: "plane", width: targetW, height: targetH });
          } catch {}
        },
      });
    }
  }, []);

  useEffect(() => {
    if (!onApi) return;
    onApi({
      flip: async () => {
        if (starting) return { ok: false, shouldFaceUser };
        try {
          await stopAR();
          const next = !shouldFaceUser;
          setShouldFaceUser(next);
          await startAR({ force: true, faceUser: next });
          return { ok: true, shouldFaceUser: next };
        } catch (e) {
          console.error("Flip failed:", e);
          setError(e?.message || "Flip camera failed.");
          return { ok: false, shouldFaceUser };
        }
      },
      getFacing: () => shouldFaceUser,
      getSourceVideo: () => {
        const sceneEl = sceneRef.current;
        if (!sceneEl) return null;
        if (shouldFaceUser) {
          return sceneEl.systems?.["mindar-face-system"]?.video || null;
        }
        return rearAssetVideoRef.current || null;
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onApi, shouldFaceUser, starting]);

  useEffect(() => {
    const sceneEl = sceneRef.current;
    if (!sceneEl) return;
    const onLoaded = () => {
      const canvas =
        sceneEl.canvas ||
        sceneEl.renderer?.domElement ||
        sceneEl.querySelector("canvas.a-canvas");
      if (canvas instanceof HTMLCanvasElement) onCanvasReady?.(canvas);
    };
    sceneEl.addEventListener("loaded", onLoaded);
    return () => sceneEl.removeEventListener("loaded", onLoaded);
  }, [onCanvasReady]);

  const ensureRearNodes = () => {
    const sceneEl = sceneRef.current;
    if (!sceneEl) return;

    if (!rearAssetVideoRef.current) {
      const assets = sceneEl.querySelector("a-assets");
      if (!assets) return;
      let assetVideo = assets.querySelector("#rearStream");
      if (!assetVideo) {
        assetVideo = document.createElement("video");
        assetVideo.id = "rearStream";
        assetVideo.setAttribute("playsinline", "");
        assetVideo.setAttribute("webkit-playsinline", "");
        assetVideo.setAttribute("muted", "");
        assetVideo.setAttribute("autoplay", "");
        assetVideo.style.display = "none";
        assets.appendChild(assetVideo);
      }
      rearAssetVideoRef.current = assetVideo;
    }

    if (!rearVideoNodeRef.current) {
      let videoNode = sceneEl.querySelector("#rear-feed");
      if (!videoNode) {
        videoNode = document.createElement("a-video");
        videoNode.setAttribute("id", "rear-feed");
        videoNode.setAttribute("src", "#rearStream");
        videoNode.setAttribute("position", "0 0 -2");
        videoNode.setAttribute("material", "shader: flat; src: #rearStream");
        videoNode.setAttribute("fit-video-plane", "video: #rearStream; mode: contain");
        videoNode.setAttribute("visible", "false");
        sceneEl.appendChild(videoNode);
      }
      rearVideoNodeRef.current = videoNode;
    }
  };

  const stopAR = async () => {
    const sceneEl = sceneRef.current;
    if (!sceneEl) return;

    try {
      const arSystem = sceneEl.systems?.["mindar-face-system"];
      if (arSystem && typeof arSystem.stop === "function") await arSystem.stop();
    } catch {}
    try {
      if (sceneEl.hasAttribute("mindar-face")) sceneEl.removeAttribute("mindar-face");
    } catch {}

    try {
      rearStreamRef.current?.getTracks?.().forEach((t) => t.stop());
    } catch {}
    rearStreamRef.current = null;

    const assetVideo = rearAssetVideoRef.current;
    if (assetVideo) {
      try { assetVideo.pause(); } catch {}
      try { assetVideo.srcObject = null; } catch {}
    }

    if (rearVideoNodeRef.current) {
      rearVideoNodeRef.current.setAttribute("visible", "false");
    }

    setStarted(false);
  };

  const startAR = async ({ force = false, faceUser } = {}) => {
    if ((starting || started) && !force) return;
    setStarting(true);
    setError("");

    const sceneEl = sceneRef.current;
    try {
      const useFront = typeof faceUser === "boolean" ? faceUser : shouldFaceUser;
      ensureRearNodes();

	  let micStream = null;
		try {
		micStream = await navigator.mediaDevices.getUserMedia({
			audio: {
			echoCancellation: true,
			noiseSuppression: true,
			autoGainControl: true,
			},
			video: false,
		});
		onPrimeMic?.(micStream);
		} catch (e) {
		console.warn("[AFrameContainer] mic permission denied:", e);
		}

      if (useFront) {
        if (rearVideoNodeRef.current) rearVideoNodeRef.current.setAttribute("visible", "false");

        sceneEl.setAttribute("mindar-face", { autoStart: false, shouldFaceUser: true, audio: true });
        const arSystem = sceneEl.systems?.["mindar-face-system"];
        if (!arSystem) throw new Error("MindAR face system not found.");
        await Promise.all([arSystem.start?.(), onArStarted?.()]);
      } else {
        try {
          const arSystem = sceneEl.systems?.["mindar-face-system"];
          if (arSystem && typeof arSystem.stop === "function") await arSystem.stop();
        } catch {}
        try {
          if (sceneEl.hasAttribute("mindar-face")) sceneEl.removeAttribute("mindar-face");
        } catch {}

        const assetVideo = rearAssetVideoRef.current;
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } },
          audio: false,
        });
        rearStreamRef.current = stream;
        assetVideo.srcObject = stream;

        try { await assetVideo.play(); } catch {}

        if (rearVideoNodeRef.current) {
          rearVideoNodeRef.current.setAttribute("visible", "true");
        }

        await onArStarted?.();
      }

      setStarted(true);
    } catch (e) {
      console.error("[AFrameContainer] startAR failed:", e);
      setError(e?.message || "Failed to start AR.");
      setStarted(false);
    } finally {
      setStarting(false);
    }
  };

  useEffect(() => {
    return () => { stopAR(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isFront = shouldFaceUser;

  return (
    <div style={{ position: "relative", width: "100%", height: "100%", zIndex: started ? 1 : 100 }}>
      {!started && (
        <div className="start-overlay">
			<img src="/assets/images/frame/frame1.png" style={{ top: 0, left: 0, position: "absolute", width: "100%"}} alt="top frame"></img>
			<button
				className="play-btn"
				onClick={() => startAR()}
				disabled={starting}
				aria-label="Start AR"
			>
				<img src="/assets/images/camera-btn.png" alt="Play" />
			</button>
			<img src="/assets/images/frame/frame2.png" style={{ bottom: 0, left: 0, position: "absolute", width: "100%"}} alt="bottom frame"></img>

          {error && <div className="error-text">{error}</div>}
        </div>
      )}

      <a-scene
        ref={sceneRef}
        embedded
        screenshot
        vr-mode-ui="enabled: false"
        device-orientation-permission-ui="enabled: false"
        renderer="colorManagement: true; physicallyCorrectLights: true; antialias: true; alpha: true"
        style={{ width: "100%", height: "100%" }}
        {...(isFront ? { "mindar-face": "autoStart: false" } : {})}
      >
        <a-assets timeout="60000">
          <a-asset-item id="bunny-ears" src="/assets/model/bunny_ears_v03.gltf"></a-asset-item>
        </a-assets>

        <a-entity light="type: ambient; intensity: 4"></a-entity>

        <a-camera look-controls="enabled: false" position="0 0 0">
          <a-entity
            id="cam-light"
            light="type: spot; intensity: 2; angle: 35; penumbra: 0.35; distance: 4; decay: 2"
            position="0 0 0"
          ></a-entity>
        </a-camera>

        {isFront ? (
          <>
            <a-entity id="occluder-wrap">
              <a-entity id="face-occluder" mindar-face-occluder></a-entity>
            </a-entity>

            <a-entity mindar-face-target="anchorIndex: 10">
              <a-entity id="head-occluder" depth-occluder="renderOrder: 0">
                <a-sphere
                  radius="0.1"
                  position="0 -0.4 -0.12"
                  scale="8 6 2"
                  material="transparent: true; opacity: 0; depthTest: true; depthWrite: true; side: double"
                ></a-sphere>
              </a-entity>

              <a-entity position="0 0.18 -0.02">
                <a-gltf-model
                  src="#bunny-ears"
                  scale="15 15 15"
                  rotation="0 -180 0"
                  position="0 -1.6 -0.7"
                ></a-gltf-model>
              </a-entity>
            </a-entity>
          </>
        ) : (
          <>
            <a-entity id="rear-scene">
              <a-entity
                geometry="primitive: plane; width: 4; height: 3"
                material="color: black; opacity: 0"
                position="0 0 -2"
              ></a-entity>
            </a-entity>
          </>
        )}
      </a-scene>
    </div>
  );
};

export default AFrameContainer;
